import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ShieldCheck, ShieldWarning, Globe, Question } from "@phosphor-icons/react";
import { APP_VERSION } from "../version";

export default function SiteFooter() {
    const [settings, setSettings] = useState(null);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [s, c] = await Promise.all([
                    api.get("/settings"),
                    api.get("/client-info"),
                ]);
                if (cancelled) return;
                setSettings(s.data);
                setInfo(c.data);
            } catch {/* fail silently — footer is non-critical */}
        })();
        return () => { cancelled = true; };
    }, []);

    const links = settings?.footer_links || [];
    return (
        <footer
            className="fixed bottom-0 inset-x-0 z-40 text-white shadow-[0_-4px_18px_-6px_rgba(15,23,42,0.25)]"
            data-testid="site-footer"
        >
            {/* Patriotic accent stripe — smooth red → white → blue gradient (visible across all background tones) */}
            <div
                className="h-[3px] w-full bg-gradient-to-r from-rose-600 via-white to-civic-500"
                aria-hidden="true"
            />
            <div className="bg-gradient-to-r from-civic-900 via-civic-800 to-civic-900 border-t border-civic-700/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
                    <div className="text-[11px] sm:text-xs leading-snug min-w-0 flex-1">
                        <div className="truncate text-civic-50/95" data-testid="footer-text">
                            {settings?.footer_text || "\u00A0"}
                            <span
                                className="ml-2 hash-mono text-[10px] text-civic-200/70"
                                data-testid="footer-version"
                                title={`E-VoteVerify version ${APP_VERSION}`}
                            >
                                v{APP_VERSION}
                            </span>
                        </div>
                        {(links.length > 0 || settings?.support_email) && (
                            <div className="hidden sm:flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-[11px]">
                                {links.map((l, i) => {
                                    const isInternal = typeof l.url === "string" && l.url.startsWith("/");
                                    if (isInternal) {
                                        return (
                                            <Link key={i} to={l.url} className="text-emerald-300 hover:text-white hover:underline transition-colors" data-testid={`footer-link-${i}`}>{l.label}</Link>
                                        );
                                    }
                                    return (
                                        <a key={i} href={l.url} target="_blank" rel="noreferrer" className="text-emerald-300 hover:text-white hover:underline transition-colors" data-testid={`footer-link-${i}`}>{l.label}</a>
                                    );
                                })}
                                {settings?.support_email && (
                                    <a href={`mailto:${settings.support_email}`} className="text-emerald-300 hover:text-white hover:underline transition-colors" data-testid="footer-support-email">{settings.support_email}</a>
                                )}
                            </div>
                        )}
                    </div>
                    <ClientBadge info={info} show={settings?.show_ip_badge !== false} />
                </div>
            </div>
        </footer>
    );
}

function ClientBadge({ info, show }) {
    if (!show || !info) return null;
    const flagged = info.is_vpn || info.is_hosting;
    const label = info.is_vpn ? "VPN / proxy" : info.is_hosting ? "Datacenter" : "Clean";
    const Icon = flagged ? ShieldWarning : info.private ? Question : ShieldCheck;
    const tone = flagged
        ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
        : info.private
            ? "border-white/15 bg-white/5 text-civic-100"
            : "border-emerald-400/50 bg-emerald-500/15 text-emerald-200";
    const ipText = info.private ? "private" : info.ip || "unknown";
    const where = !info.private && info.country_code ? ` · ${info.country_code}` : "";

    return (
        <div className={`hash-mono text-[10px] sm:text-[11px] flex items-center gap-1 px-1.5 py-0.5 rounded-sm border shrink-0 backdrop-blur-sm ${tone}`} data-testid="client-ip-badge">
            <Globe size={11} weight="bold" />
            <span data-testid="client-ip-value">{ipText}</span>
            {where && <span className="hidden sm:inline opacity-80">{where}</span>}
            <span className="opacity-60">·</span>
            <Icon size={11} weight={flagged ? "fill" : "bold"} />
            <span data-testid="client-vpn-flag">{label}</span>
        </div>
    );
}
